import React, { useState, useMemo } from 'react';
import { SECTIONS, UI_STRINGS, LANGS } from '../../data/helpContent';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Gauge, Users, Factory, Car, Truck, Package, UserGear, TelegramLogo,
  PlugsConnected, ChartBar, Warehouse, Anchor, Stack, Receipt, CarProfile,
  Question, FilePdf, Printer,
} from '@phosphor-icons/react';

const ICON_MAP = {
  Gauge, Users, Factory, Car, Truck, Package, UserGear, TelegramLogo,
  PlugsConnected, ChartBar, Warehouse, Anchor, Stack, Receipt, CarProfile,
};

const API = process.env.REACT_APP_BACKEND_URL;

const Callout = ({ n, c, lang }) => (
  <div
    className="absolute z-10 group"
    style={{ left: `${c.x}%`, top: `${c.y}%`, transform: 'translate(-50%,-50%)' }}
  >
    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center border-2 border-white shadow-lg cursor-help">
      {n}
    </div>
    <div className="opacity-0 group-hover:opacity-100 transition pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap z-20 shadow-xl">
      {c.label[lang] || c.label.en}
    </div>
  </div>
);

const HelpCenterPage = () => {
  const [lang, setLang] = useState('en');
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [search, setSearch] = useState('');

  const active = SECTIONS.find(s => s.id === activeId) || SECTIONS[0];

  const filteredSections = useMemo(() => {
    if (!search.trim()) return SECTIONS;
    const q = search.trim().toLowerCase();
    return SECTIONS.filter(s =>
      (s.title.en + ' ' + s.title.fr + ' ' + (s.intro.en || '') + ' ' + (s.intro.fr || ''))
        .toLowerCase()
        .includes(q)
    );
  }, [search]);

  const t = (k) => UI_STRINGS[k]?.[lang] || UI_STRINGS[k]?.en || k;

  const sectionPdfUrl = `${API}/api/help/pdf?section=${activeId}&lang=${lang}`;
  const fullPdfUrl = `${API}/api/help/pdf?lang=${lang}`;

  return (
    <div data-testid="help-center-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Question weight="duotone" className="w-7 h-7 text-primary" />
            {t('pageTitle')}
          </h1>
          <p className="text-sm text-muted-foreground">{SECTIONS.length} sections · {LANGS[lang]}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Language toggle */}
          <div className="inline-flex border rounded overflow-hidden">
            {Object.entries(LANGS).map(([code, label]) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                className={`px-3 py-1.5 text-xs font-semibold transition ${lang === code ? 'bg-primary text-primary-foreground' : 'bg-white hover:bg-slate-50'}`}
                data-testid={`help-lang-${code}`}
              >
                {label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => window.open(fullPdfUrl, '_blank')} data-testid="help-full-pdf-btn">
            <FilePdf className="w-4 h-4 mr-1" /> {t('downloadFullPdf')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar */}
        <div className="col-span-12 md:col-span-3 lg:col-span-3">
          <div className="bg-white border rounded p-2 sticky top-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="h-8 text-sm mb-2"
              data-testid="help-search"
            />
            <div className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filteredSections.map(s => {
                const Icon = ICON_MAP[s.icon] || Question;
                const isActive = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveId(s.id)}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 text-sm rounded transition ${
                      isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-slate-100'
                    }`}
                    data-testid={`help-nav-${s.id}`}
                  >
                    <Icon weight="duotone" className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{s.title[lang] || s.title.en}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="col-span-12 md:col-span-9 lg:col-span-9 space-y-4 print:col-span-12">
          <div className="bg-white border rounded p-5" data-testid="help-section-content">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <h2 className="text-xl font-bold">{active.title[lang]}</h2>
                <p className="text-sm text-muted-foreground mt-1">{active.intro[lang]}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => window.print()} className="print:hidden">
                  <Printer className="w-4 h-4 mr-1" /> {t('printable')}
                </Button>
                <Button size="sm" onClick={() => window.open(sectionPdfUrl, '_blank')} data-testid={`help-pdf-${active.id}`} className="print:hidden">
                  <FilePdf className="w-4 h-4 mr-1" /> {t('downloadPdf')}
                </Button>
              </div>
            </div>

            {/* Screenshot with callouts */}
            <div className="relative bg-slate-100 border rounded overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <img
                src={`/help-screenshots/${active.screenshot}`}
                alt={active.title[lang]}
                className="w-full h-full object-contain"
                onError={(e) => { e.target.style.opacity = 0.2; }}
              />
              {(active.callouts || []).map((c, i) => (
                <Callout key={i} n={i + 1} c={c} lang={lang} />
              ))}
              {(!active.callouts || active.callouts.length === 0) && (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                  Screenshot pending
                </div>
              )}
            </div>

            {/* Callout legend */}
            {active.callouts && active.callouts.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  {t('callout')}
                </h3>
                <ol className="space-y-2">
                  {active.callouts.map((c, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground font-bold text-xs flex items-center justify-center">{i + 1}</span>
                      <div>
                        <div className="font-semibold">{c.label[lang]}</div>
                        <div className="text-muted-foreground text-xs">{c.text[lang]}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Steps */}
            {active.steps && active.steps.length > 0 && (
              <div className="mt-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  {t('steps')}
                </h3>
                <ol className="space-y-2">
                  {active.steps.map((s, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-200 text-foreground font-bold text-xs flex items-center justify-center">{i + 1}</span>
                      <div>{s[lang]}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Permissions */}
            {active.permissions && (
              <div className="mt-5 bg-amber-50 border-l-4 border-amber-400 px-3 py-2">
                <div className="text-[10px] font-bold uppercase text-amber-700 mb-0.5">{t('permissions')}</div>
                <div className="text-xs text-amber-900">{active.permissions[lang]}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;
